"use client";

import React from "react";
import { useStoryblokLivePage } from "hooks/useStoryblokLivePage";
import { ORGANISM_MAP } from "./organism-map";
import { withFilledSlots } from "./slot-fillers";
import {
  ABOVE_THE_FOLD_COUNT,
  NON_COUNTABLE_TYPES,
  resolveSectionIds
} from "./section-rules";
import { OrganismError } from "./organism-error";
import { OrganismBoundary } from "./organism-boundary.client";

/**
 * Storyblok Visual Editor canvas for v3.
 *
 * ⚠ This is a SECOND render path, client-side, and only the editor uses it.
 * It exists because `useStoryblokLivePage` re-runs `pageAdapter` in the
 * browser on every keystroke, and `V3MainCanvas` is an async Server
 * Component — a client module cannot render one. Production is unaffected:
 * nothing outside the preview branch imports this.
 *
 * Two deliberate divergences from the server canvas, both editor-only:
 *
 * 1. Organisms are rendered as elements, not called, so a BROWSER throw in an
 *    organism or its slot filler is caught by its section's OrganismBoundary.
 *    This canvas is also server-rendered, where no boundary runs: see
 *    app/v3/README.md "Error isolation".
 * 2. DDS organisms ship to the browser in preview. They carry no directive,
 *    so in a client module graph they compile as client components. Correct
 *    for an editor; it is not what a customer downloads.
 */

/* Runs the slot filler inside the boundary's child render, so a filler that
   throws on a partial, mid-keystroke blok costs its section, not the editor. */
function FilledOrganism({
  Organism,
  type,
  organismProps,
  ownProps
}: {
  Organism: React.ElementType;
  type: string;
  organismProps: Record<string, unknown>;
  ownProps: Record<string, unknown>;
}) {
  return <Organism {...withFilledSlots(type, organismProps)} {...ownProps} />;
}

export interface V3PreviewCanvasProps {
  /** Adapter inputs for the live re-adapt — `{ pagesCache, pagesInfo, …feeds }`. */
  pageAdapterProps?: any;
  sbPreviewAllowed: boolean;
  sbRelationResolveKey?: string;
  /** Server-rendered components, used until the editor sends its first update. */
  initialComponents?: any[];
  pagePath?: string;
}

export default function V3PreviewCanvas({
  pageAdapterProps,
  sbPreviewAllowed,
  sbRelationResolveKey,
  initialComponents = []
}: V3PreviewCanvasProps) {
  const { components: liveComponents } = useStoryblokLivePage({
    sbPreviewAllowed,
    pageAdapterProps,
    initialComponents,
    sbRelationResolveKey
  });

  const components = sbPreviewAllowed ? liveComponents : initialComponents;
  let countable = -1;

  return (
    <>
      {(components || []).map((component: any, index: number) => {
        if (!component?.type) {
          return null;
        }

        const Organism = ORGANISM_MAP[component.type as keyof typeof ORGANISM_MAP];
        const { id: authoredId, ...organismProps } = component?.props || {};
        const { sectionId: id, organismId } = resolveSectionIds(
          component.type,
          authoredId,
          index
        );
        /* Storyblok click-to-edit attributes ride on the wrapper, because DDS
           organisms do not pass unknown props through to their DOM. */
        const editableProps =
          component?.editableProps &&
          typeof component.editableProps === "object" &&
          !Array.isArray(component.editableProps) ?
            component.editableProps :
            {};

        if (!Organism) {
          // Loud in the editor on purpose: an unmapped blok is exactly what an
          // author needs told, and silence would read as "my content is gone".
          return (
            <section
              {...editableProps}
              data-section={component.type}
              id={id}
              key={id}
            >
              <OrganismError
                detail={`No v3 organism mapped for "${component.type}"`}
                type={component.type}
              />
            </section>
          );
        }

        // Same slot rule as the server canvas, so the editor shows what live does.
        const counts = !NON_COUNTABLE_TYPES?.includes?.(component.type);
        if (counts) {
          countable += 1;
        }
        const aboveTheFold = counts && countable < ABOVE_THE_FOLD_COUNT;

        return (
          <section
            {...editableProps}
            data-section={component.type}
            id={id}
            key={id}
          >
            {/* `id` is held back for the same reason as the server canvas:
                the <section> above already carries the anchor. */}
            {/* ⚠ Same filler as the server canvas, or the editor shows a slot-less
                section and the author reports a bug that does not exist live. */}
            <OrganismBoundary type={component.type}>
              <FilledOrganism
                Organism={Organism}
                organismProps={organismProps}
                ownProps={{
                  ...(organismId !== undefined && { id: organismId }),
                  aboveTheFold,
                  isAboveTheFold: aboveTheFold
                }}
                type={component.type}
              />
            </OrganismBoundary>
          </section>
        );
      })}
    </>
  );
}
